/**
 * VariableOperatorService
 *
 * Maps VariableType to valid ComparisonOperators.
 * Unlike V1 (screen-type-based), V2 maps by variable type.
 *
 * @module v2-variable-utils
 */

import type { ComparisonOperator, VariableType } from '@segmently/schema-models/v2';
import type { V2OperatorInfo } from '../types/variable-utils.types';
import {
  VARIABLE_TYPE_TO_OPERATORS,
  V2_OPERATOR_INFO,
  VALUELESS_OPERATORS,
  MULTI_VALUE_OPERATORS,
} from '../constants/variable-type-operators';

export class VariableOperatorService {
  /**
   * Get valid operators for a VariableType, with UI metadata.
   *
   * @param variableType - The variable type (e.g., 'enum', 'number')
   * @returns Array of operator info objects
   */
  getOperatorsForType(variableType: VariableType): V2OperatorInfo[] {
    const operators = VARIABLE_TYPE_TO_OPERATORS[variableType];
    if (!operators) {
      return [];
    }
    return operators.map((op) => V2_OPERATOR_INFO[op]);
  }

  /**
   * Get valid operator values for a VariableType (without UI metadata).
   */
  getOperatorValuesForType(variableType: VariableType): ComparisonOperator[] {
    const operators = VARIABLE_TYPE_TO_OPERATORS[variableType];
    return operators ? [...operators] : [];
  }

  /**
   * Check if operator is valid for a variable type.
   */
  isOperatorValid(operator: ComparisonOperator, variableType: VariableType): boolean {
    const operators = VARIABLE_TYPE_TO_OPERATORS[variableType];
    return operators ? operators.includes(operator) : false;
  }

  /**
   * Get operator info by operator value.
   */
  getOperatorInfo(operator: ComparisonOperator): V2OperatorInfo {
    return V2_OPERATOR_INFO[operator];
  }

  /**
   * Check if operator requires a comparison value.
   * Returns false for exists/notExists — no value input needed.
   */
  operatorRequiresValue(operator: ComparisonOperator): boolean {
    return !VALUELESS_OPERATORS.includes(operator);
  }

  /**
   * Check if operator requires multiple values (comma-separated).
   * True for in, notIn, containsAll, containsAny.
   */
  operatorRequiresMultiValue(operator: ComparisonOperator): boolean {
    return MULTI_VALUE_OPERATORS.includes(operator);
  }
}

/** Singleton instance */
export const variableOperatorService = new VariableOperatorService();
